import {useCallback, useEffect, useRef, useState} from "react";
import {getCandidates} from "../services/candidateApi";

const candidateLabel = (candidate) => candidate?.full_name || `Кандидат № ${candidate?.id}`;

export default function useCandidateAutocomplete({debounceMs = 300, limit = 20} = {}) {
    const [query, setQueryState] = useState("");
    const [candidates, setCandidates] = useState([]);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");
    const requestSequence = useRef(0);
    const activeController = useRef(null);

    useEffect(() => {
        const normalized = query.trim();
        if (selectedCandidate && query === candidateLabel(selectedCandidate)) {
            setCandidates([selectedCandidate]);
            setStatus("success");
            return undefined;
        }
        if (normalized.length === 1) {
            setCandidates([]);
            setStatus("idle");
            setError("");
            return undefined;
        }

        const sequence = ++requestSequence.current;
        const controller = new AbortController();
        activeController.current?.abort();
        activeController.current = controller;
        const timer = setTimeout(async () => {
            setStatus("loading");
            setError("");
            try {
                const result = await getCandidates(
                    null,
                    normalized || null,
                    null,
                    null,
                    undefined,
                    0,
                    limit,
                    null,
                    {signal: controller.signal},
                );
                if (sequence !== requestSequence.current || controller.signal.aborted) return;
                setCandidates(Array.isArray(result) ? result : (result?.items || []));
                setStatus("success");
            } catch (requestError) {
                if (requestError?.name === "AbortError" || controller.signal.aborted || sequence !== requestSequence.current) return;
                setCandidates([]);
                setStatus("error");
                setError(requestError?.message || "Не удалось загрузить кандидатов");
            }
        }, normalized ? debounceMs : 0);

        return () => {
            clearTimeout(timer);
            controller.abort();
            if (activeController.current === controller) activeController.current = null;
        };
    }, [query, selectedCandidate, debounceMs, limit]);

    const setQuery = useCallback((value) => {
        activeController.current?.abort();
        requestSequence.current += 1;
        setQueryState(value);
        setSelectedCandidate((current) => current && value === candidateLabel(current) ? current : null);
        setCandidates([]);
        setStatus(value.trim().length === 1 ? "idle" : "loading");
        setError("");
    }, []);

    const selectCandidate = useCallback((candidate) => {
        setSelectedCandidate(candidate);
        setQueryState(candidateLabel(candidate));
        setCandidates([candidate]);
        setStatus("success");
        setError("");
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedCandidate(null);
        setQueryState("");
        setError("");
    }, []);

    return {
        query,
        setQuery,
        candidates,
        selectedCandidate,
        selectCandidate,
        clearSelection,
        status,
        error,
    };
}

export {candidateLabel};
